import { useEffect, useRef, useState } from "react";
import VoiceSearchInput from "../../form/voice-search-input";
import QuestionCard from "../../UI/question-card";
import ModalPageWrapper from "../../global-layout/modal-page-wrapper";
import IconBtn from "../../form/icon-btn";
import { PageModalCloseIcon } from "../../svg";
import Skeleton from "react-loading-skeleton";
import ImageEditor from "../../UI/image-editor";
import defaultImage from "../../../assets/images/default.png";

const getUrlWithSasToken = (url: any, sasToken: any) => {
  if (!url || typeof url !== "string") return url;
  if (!sasToken || typeof sasToken !== "string" || !sasToken.startsWith("?")) return url;
  if (url.includes("?")) return url;

  return `${url}${sasToken}`;
};

const getAllAnnotatedDetails = (value: any, seen = new WeakSet<object>()): any[] => {
  if (!value || typeof value !== "object") return [];
  if (seen.has(value)) return [];
  seen.add(value);

  if (Array.isArray(value)) {
    return value.flatMap((item) => getAllAnnotatedDetails(item, seen));
  }

  return [
    ...(Array.isArray(value.annotatedDetails) ? value.annotatedDetails : []),
    ...getAllAnnotatedDetails(value.inputDetails, seen),
    ...getAllAnnotatedDetails(value.labelValue, seen),
    ...getAllAnnotatedDetails(value.defaultAnswer, seen),
  ];
};

const getIgnoredLabelIds = (interactionConfigurations: any) => {
  const ignoredLabels = interactionConfigurations?.ignoreDefaultLabels;
  if (typeof ignoredLabels !== "string") return [];

  return ignoredLabels
    .split(",")
    .map((labelId: string) => labelId.trim())
    .filter(Boolean);
};

const AnnotatedImagePreview = ({ imageSource, handleImageError }: any) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageSize, setImageSize] = useState({
    width: 0,
    height: 0,
    naturalWidth: 0,
    naturalHeight: 0,
  });
  const normalizedImageSource = Array.isArray(imageSource) ? imageSource[0] : imageSource;
  const imageUrl =
    typeof normalizedImageSource === "string"
      ? normalizedImageSource
      : normalizedImageSource?.annotatedUrl ||
        getUrlWithSasToken(normalizedImageSource?.url, normalizedImageSource?.sasToken) ||
        normalizedImageSource?.imageUrl;
  const annotatedDetails =
    typeof normalizedImageSource === "object"
      ? getAllAnnotatedDetails(normalizedImageSource)
      : [];
  const ignoredLabelIds = getIgnoredLabelIds(
    typeof normalizedImageSource === "object"
      ? normalizedImageSource?.interactionConfigurations
      : null
  );
  const visibleAnnotatedDetails = annotatedDetails.filter((annotation: any) => {
    const labelUniqueId = annotation?.labelUniqueId || annotation?.modelValidationDetails?.label;
    return !ignoredLabelIds.includes(labelUniqueId);
  });
  const uniqueAnnotatedDetails = visibleAnnotatedDetails.filter(
    (annotation: any, index: number, list: any[]) =>
      index ===
      list.findIndex(
        (item: any) =>
          (item.uniqueId || item.id || `${item.labelName}-${JSON.stringify(item.coordinates)}`) ===
          (annotation.uniqueId || annotation.id || `${annotation.labelName}-${JSON.stringify(annotation.coordinates)}`)
      )
  );
  const updateImageSize = () => {
    const image = imageRef.current;
    if (!image) return;

    setImageSize({
      width: image.clientWidth,
      height: image.clientHeight,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
    });
  };

  useEffect(() => {
    updateImageSize();
    window.addEventListener("resize", updateImageSize);

    return () => {
      window.removeEventListener("resize", updateImageSize);
    };
  }, [imageUrl]);

  const getAnnotationRect = (annotation: any) => {
    const coordinates = annotation?.coordinates;
    if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
    if (!imageSize.width || !imageSize.height || !imageSize.naturalWidth || !imageSize.naturalHeight) {
      return null;
    }

    const [start, end] = coordinates;
    const scaleX = imageSize.width / imageSize.naturalWidth;
    const scaleY = imageSize.height / imageSize.naturalHeight;
    const x1 = Number(start?.x || 0) * scaleX;
    const y1 = Number(start?.y || 0) * scaleY;
    const x2 = Number(end?.x || 0) * scaleX;
    const y2 = Number(end?.y || 0) * scaleY;

    return {
      x: Math.min(x1, x2),
      y: Math.min(y1, y2),
      width: Math.abs(x2 - x1),
      height: Math.abs(y2 - y1),
    };
  };

  return (
    <div className="relative inline-block max-w-full">
      <img
        ref={imageRef}
        className="max-w-full max-h-[60vh] object-contain object-center"
        src={imageUrl}
        alt="Preview"
        onLoad={updateImageSize}
        onError={(e) => handleImageError(e)}
      />
      {uniqueAnnotatedDetails.length > 0 &&
        imageSize.width > 0 &&
        imageSize.height > 0 &&
        uniqueAnnotatedDetails.map((annotation: any, index: number) => {
          const rect = getAnnotationRect(annotation);
          if (!rect || annotation?.isDeleted === 1) return null;
          const stroke = annotation.stroke || annotation.hexaColor || "#EF4444";
          const labelName = annotation.labelName || annotation.viewDetails?.part || `Annotation ${index + 1}`;

          return (
            <div
              key={annotation.uniqueId || annotation.id || index}
              className="absolute pointer-events-none z-10"
              style={{
                left: rect.x,
                top: rect.y,
                width: rect.width,
                height: rect.height,
                border: `${Math.max(Number(annotation.strokeWidth || 2), 3)}px solid ${stroke}`,
              }}
            >
              <div
                className="absolute left-0 bottom-full mb-1 whitespace-nowrap px-2 py-1 text-[1.2rem] font-semibold leading-none"
                style={{ backgroundColor: stroke, color: "#111827" }}
              >
                {labelName}
              </div>
            </div>
          );
        })}
    </div>
  );
};

function OptionOneMain({
  allProcessData,
  setAllProcessData,
  isLoading,
  isViewOnly,
  validationErrors,
}: any) {
  const processData = allProcessData?.screenDetail?.mainScreenDetails;
  
  // Check if processData is defined and has at least one element
  const initialScreen =
    processData && processData.length > 0 ? processData[0] : null;
  const initialScreenId = initialScreen ? initialScreen.id : null;
  const [selectedScreen, setSelectedScreen] = useState<any>(initialScreen);
  const [selectedScreenId, setSelectedScreenId] =
    useState<any>(initialScreenId);
  const [showImagePreview, setShowImagePreview] = useState<boolean>(false);
  const [previewImageSrc, setPreviewImageSrc] = useState<any>(null);
  const [tabSearchData, setTabSearchData] = useState<any>(processData || []);
  const [previewImageUrl, setPreviewImageUrl] = useState(undefined);
  const [previewImageData, setPreviewImageData] = useState<any>(null);
  const [previewImgModal, setPreviewImgModal] = useState(false);
  useEffect(() => {
    // Update state when processData changes, if needed
    if (processData && processData.length > 0) {
      setSelectedScreen(processData[0]);
      setSelectedScreenId(processData[0].id);
      setTabSearchData(processData);
    }
  }, [processData]);

  const handleScreenClick = (screen: any) => {
    setSelectedScreen(screen);
    setSelectedScreenId(screen.id);
  };

  useEffect(() => {}, [selectedScreen]);

  const expandPreviewImage = (status: boolean, data: any) => {
    setShowImagePreview(status);
    setPreviewImageSrc(data.url);
  };
  const handleSearch = (e: any) => {
    const value = e.target.value;
    filterTabSearchData(value);
  };

  const filterTabSearchData = (searchQuery: string) => {
    const filteredData = processData.filter((item: any) => {
      return item.title.toLowerCase().includes(searchQuery.toLowerCase());
    });
    setTabSearchData(filteredData);
  };
  const handleImagePreview = (imageData: any) => {
    const imageSource = Array.isArray(imageData) ? imageData[0] : imageData;
    const imageUrl =
      typeof imageSource === "string"
        ? imageSource
        : imageSource?.annotatedUrl ||
          getUrlWithSasToken(imageSource?.url, imageSource?.sasToken) ||
          imageSource?.imageUrl;
    setPreviewImgModal(true);
    setPreviewImageData(imageData);
    setPreviewImageUrl(imageUrl);
  };
  const handleImageError = (event: any) => {
    event.target.src = defaultImage;
  };
  return (
    <>
      <div className="w-full h-auto flex gap-20 max-xl:gap-4 relative pt-10">
        <div className="w-[34rem] max-xl:w-[28rem] process-sidenav sticky top-[10.5rem]">
          {isLoading ? (
            <Skeleton width="100%" height="100%" borderRadius={15} />
          ) : (
            <div className="w-full h-full flex flex-col rounded-[1.5rem] border border-io-gray-df dark:border-io-black-48 bg-io-white dark:bg-[#1A1C28] pb-[1.4rem]">
              <div className="w-full h-auto px-[2rem] py-[1.4rem] border-b border-io-gray-df dark:border-io-black-48">
                <p className="text-[1.6rem] font-inter font-normal text-io-black dark:text-io-white leading-none">
                  {allProcessData?.processLogicName}
                </p>
              </div>
              <div className="w-full h-auto px-[2rem] pt-[2rem] pb-[1.4rem]">
                <VoiceSearchInput
                  withMic={false}
                  inputSize="small"
                  handleSearch={handleSearch}
                />
              </div>
              <div className="w-full flex-auto basis-0 overflow-y-auto scrollbar-medium px-[2rem]">
                {tabSearchData?.length > 0 ? (
                  tabSearchData.map((item: any, i: number) => (
                    <div
                      key={i}
                      className="w-full h-auto mb-2 cursor-pointer group"
                      onClick={() => handleScreenClick(item)}
                    >
                      <div
                        className={`w-full h-auto px-[1.2rem] py-4 rounded-[0.3rem] ${
                          selectedScreen.id === item.id
                            ? "bg-[#F2F8FF] dark:bg-[#182233]"
                            : "bg-io-white dark:bg-transparent"
                        } group-hover:bg-[#F2F8FF] dark:group-hover:bg-[#182233]`}
                      >
                        <p
                          className={`text-[1.4rem] font-inter font-normal ${
                            selectedScreen.id === item.id
                              ? "text-io-primary"
                              : "text-io-gray-66 dark:text-io-black-d1"
                          } ${
                            validationErrors[item.id]
                              ? "text-red-500 group-hover:text-red-500"
                              : "group-hover:text-io-primary"
                          }`}
                        >
                          {item.title}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-[1.6rem] font-inter font-normal text-io-gray-66 text-center italic">
                    No data Found
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex-1 h-auto ">
          <p className="text-[1.6rem] font-inter font-normal text-io-gray-9d italic">
            {isLoading ? (
              <Skeleton
                width={200}
                height={20}
                borderRadius={3}
                style={{ marginBottom: "10px" }}
              />
            ) : (
              <>
                {allProcessData?.screenDetail?.isOnlyMagicContainer !== 1 && (
                selectedScreen?.questionDetails.length > 1 && (
                  <span>
                    {selectedScreen?.questionDetails?.length} Entries Found
                  </span>
                )
              )}
              </>
            )}
          </p>

          {selectedScreen?.questionDetails.length > 0 &&
            selectedScreen?.questionDetails.map(
              (screendetail: any, i: number) => {
                const screenId = selectedScreen.id;
                const filteredErrors = validationErrors?.[screenId] || {};
                return (
                  <QuestionCard
                    key={i}
                    item={screendetail}
                    fullProcessData={allProcessData}
                    setAllProcessData={setAllProcessData}
                    selectedScreenId={selectedScreenId}
                    index={i}
                    expandPreviewImage={expandPreviewImage}
                    isLoading={isLoading}
                    isViewOnly={isViewOnly}
                    handleImagePreview={handleImagePreview}
                    validationErrors={filteredErrors}
                    isShowQuestionCount={selectedScreen?.questionDetails.length > 1}
                    interactionConfigurations={allProcessData?.screenDetail?.interactionConfigurations}
                  />
                );
              }
            )}
        </div>
      </div>
      {showImagePreview && (
        <ModalPageWrapper>
          <div className="w-full h-full flex justify-center items-center">
            <div className="w-[60%] h-auto bg-io-white dark:bg-io-black-1a rounded-[2.2rem] max-md:w-[68%] relative">
              <div className="w-full h-auto p-12 text-center">
                <div className="w-full h-auto flex justify-end mb-5">
                  <IconBtn
                    size="w-[3rem]"
                    icon={<PageModalCloseIcon />}
                    handleClick={() => setShowImagePreview(false)}
                  />
                </div>
                <div className="w-full h-auto">
                  {previewImageSrc && (
                    <ImageEditor imageSource={previewImageSrc} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </ModalPageWrapper>
      )}
      {previewImgModal && (
        <ModalPageWrapper>
          <div className="w-full h-full flex justify-center items-center">
            <div className="w-[60%] h-auto bg-io-white dark:bg-io-black-1a rounded-[2.2rem] max-md:w-[68%] relative">
              <div className="w-full h-auto p-12 text-center">
                <div className="w-full h-auto flex justify-end mb-5">
                  <IconBtn
                    size="w-[3rem]"
                    icon={<PageModalCloseIcon />}
                    handleClick={() => setPreviewImgModal(false)}
                  />
                </div>
                <div className="w-full h-auto">
                  {previewImageUrl && (
                    <AnnotatedImagePreview
                      imageSource={previewImageData || previewImageUrl}
                      handleImageError={handleImageError}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </ModalPageWrapper>
      )}
    </>
  );
}
export default OptionOneMain;
